<template>
  <v-row>
    <v-col cols="12">
      <v-card class="cn-card" flat>
        <div class="cn-header pl-base-bg">
          <v-icon color="#fff" size="18" class="mr-3">mdi-file-document-minus-outline</v-icon>
          <div>
            <div class="cn-title">Create Credit Note</div>
            <div class="cn-subtitle">Reduce what a customer owes against an invoice</div>
          </div>
        </div>

        <v-form ref="form" v-model="valid" class="pa-6">
          <div class="cn-section-label">Credit Note Details</div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.creditNoteNumber"
                label="Credit Note Number"
                outlined
                dense
                :rules="[rules.required, rules.uniqueCreditNote]"
                prepend-inner-icon="mdi-file-document-outline"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.creditNoteDate"
                    label="Credit Note Date"
                    outlined
                    dense
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.creditNoteDate" @input="dateMenu = false" />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="4">
              <v-select
                v-model="form.reason"
                :items="reasonOptions"
                label="Reason"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12">
              <v-autocomplete
                v-model="selectedInvoice"
                :items="creditableInvoices"
                :item-text="invoiceLabel"
                return-object
                label="Against Invoice"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-file-document-outline"
                @change="onInvoiceSelect"
              />
            </v-col>
          </v-row>

          <template v-if="selectedInvoice">
            <div class="cn-invoice-summary">
              <div class="cn-invoice-summary-row">
                <span>Customer</span>
                <span>{{ selectedInvoice.customer?.name }}</span>
              </div>
              <div class="cn-invoice-summary-row">
                <span>Invoice Total</span>
                <span>{{ formatAmount(selectedInvoice.totalAmount) }}</span>
              </div>
              <div class="cn-invoice-summary-row">
                <span>Already Credited</span>
                <span>{{ formatAmount(selectedInvoice.creditedAmount) }}</span>
              </div>
              <div class="cn-invoice-summary-row cn-invoice-summary-row--highlight">
                <span>Remaining Creditable</span>
                <span>{{ formatAmount(remainingCreditable) }}</span>
              </div>
            </div>

            <div class="cn-section-label mt-4">Items</div>

            <div
              v-for="(item, index) in form.items"
              :key="index"
              class="cn-item-row"
            >
              <div class="cn-item-row-head">
                <span class="cn-item-index">Item {{ index + 1 }}</span>
                <v-btn icon x-small class="cn-item-remove" @click="removeItem(index)">
                  <v-icon color="error" size="20">mdi-delete-outline</v-icon>
                </v-btn>
              </div>

              <v-row dense>
                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model="item.product"
                    label="Product / Description"
                    dense
                    outlined
                    hide-details
                    :rules="[rules.required]"
                  />
                </v-col>

                <v-col cols="6" sm="2">
                  <v-text-field v-model="item.hsn" label="HSN/SAC" dense outlined hide-details />
                </v-col>

                <v-col cols="4" sm="1">
                  <v-text-field
                    v-model.number="item.quantity"
                    type="number"
                    label="Qty"
                    dense
                    outlined
                    hide-details
                    @input="calcItem(index)"
                  />
                </v-col>

                <v-col cols="4" sm="2">
                  <v-text-field
                    v-model.number="item.price"
                    type="number"
                    label="Rate"
                    dense
                    outlined
                    hide-details
                    @input="calcItem(index)"
                  />
                </v-col>

                <v-col cols="4" sm="2">
                  <v-text-field
                    v-model.number="item.taxPercent"
                    type="number"
                    label="Tax %"
                    dense
                    outlined
                    hide-details
                    suffix="%"
                    @input="calcItem(index)"
                  />
                </v-col>
              </v-row>

              <div class="cn-item-row-foot">
                <span class="cn-item-amount-label">Amount</span>
                <span class="cn-item-amount-value">{{ formatAmount(item.amount) }}</span>
              </div>
            </div>

            <v-btn small class="cn-add-item-btn" @click="addItem">
              <v-icon left size="16">mdi-plus</v-icon>
              Add Item
            </v-btn>

            <v-divider class="my-4" />
            <v-row dense justify="end">
              <v-col cols="12" sm="4">
                <div class="cn-total-row">
                  <span>Subtotal</span>
                  <span>{{ formatAmount(subtotal) }}</span>
                </div>
                <div class="cn-total-row">
                  <span>GST</span>
                  <span>{{ formatAmount(totalGst) }}</span>
                </div>
                <div class="cn-total-row cn-total-row--grand">
                  <span>Total</span>
                  <span>{{ formatAmount(subtotal + totalGst) }}</span>
                </div>
              </v-col>
            </v-row>

            <v-alert
              v-if="exceedsRemaining"
              type="error"
              outlined
              dense
              class="mt-2"
            >
              This credit note's total exceeds the invoice's remaining creditable amount ({{ formatAmount(remainingCreditable) }}).
            </v-alert>

            <v-alert
              v-else-if="isZeroAmount"
              type="warning"
              outlined
              dense
              class="mt-2"
            >
              Credit note total must be greater than ₹0 — enter a quantity and rate for at least one item.
            </v-alert>
          </template>

          <v-textarea v-model="form.note" label="Note" outlined dense rows="2" class="mt-4" />

          <v-row class="mt-4" justify="end" dense>
            <v-col cols="6" sm="auto">
              <v-btn block color="secondary" @click="reset">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="6" sm="auto">
              <v-btn
                block
                class="cn-save-btn"
                :loading="loading"
                :disabled="!selectedInvoice || exceedsRemaining || isZeroAmount"
                @click="submit"
              >
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "CreateCreditNote",

  data() {
    return {
      valid: false,
      loading: false,
      dateMenu: false,
      selectedInvoice: null,

      reasonOptions: [
        { text: "Sales Return", value: "SALES_RETURN" },
        { text: "Post-Sale Discount", value: "POST_SALE_DISCOUNT" },
        { text: "Billing Correction", value: "BILLING_CORRECTION" },
        { text: "Other", value: "OTHER" },
      ],

      form: {
        creditNoteNumber: "",
        creditNoteDate: new Date().toISOString().slice(0, 10),
        reason: "SALES_RETURN",
        note: "",
        items: [],
      },

      rules: {
        required: (v) => !!v || "Required",
        uniqueCreditNote: (v) => {
          if (!v || !this.creditNotes.length) return true;
          return (
            !this.creditNotes.some(
              (c) => c.creditNoteNumber?.toLowerCase() === v.toLowerCase(),
            ) || "Credit note number already exists"
          );
        },
      },
    };
  },

  created() {
    this.$store.dispatch("invoices/getInvoices");
    this.$store.dispatch("creditNotes/getCreditNotes");
    this.fetchNextNumber();

    // Support deep-linking from view-invoice.vue's "Issue Credit Note"
    // button: ?invoiceId=<id> pre-selects and locks the source invoice.
    const invoiceId = this.$route.query.invoiceId;
    if (invoiceId) {
      this.$watch(
        "creditableInvoices",
        (invoices) => {
          const match = invoices.find((inv) => inv._id === invoiceId);
          if (match && !this.selectedInvoice) {
            this.selectedInvoice = match;
            this.onInvoiceSelect(match);
          }
        },
        { immediate: true }
      );
    }
  },

  computed: {
    invoices() {
      return this.$store.state.invoices.list || [];
    },

    creditNotes() {
      return this.$store.state.creditNotes.list || [];
    },

    // Only invoices that still have something left to credit, and aren't
    // themselves cancelled.
    creditableInvoices() {
      return this.invoices.filter(
        (inv) =>
          inv.status !== "CANCELLED" &&
          inv.totalAmount - (inv.creditedAmount || 0) > 0.01
      );
    },

    remainingCreditable() {
      if (!this.selectedInvoice) return 0;
      return +(
        this.selectedInvoice.totalAmount - (this.selectedInvoice.creditedAmount || 0)
      ).toFixed(2);
    },

    subtotal() {
      return this.form.items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
    },

    totalGst() {
      return this.form.items.reduce((sum, it) => {
        const amount = Number(it.amount) || 0;
        const percent = Number(it.taxPercent) || 0;
        return sum + (amount * percent) / 100;
      }, 0);
    },

    exceedsRemaining() {
      if (!this.selectedInvoice) return false;
      return this.subtotal + this.totalGst > this.remainingCreditable + 0.01;
    },

    // A ₹0 note reduces nothing and refunds nothing — not a
    // representable real-world correction, just noise in the list.
    isZeroAmount() {
      return this.subtotal + this.totalGst <= 0.01;
    },
  },

  methods: {
    async fetchNextNumber() {
      try {
        this.form.creditNoteNumber = await this.$store.dispatch(
          "creditNotes/getNextCreditNoteNumber"
        );
      } catch (err) {
        console.error(err);
      }
    },

    invoiceLabel(invoice) {
      return `#${invoice.invoiceNumber} — ${invoice.customer?.name || "-"} (${this.formatAmount(invoice.totalAmount)})`;
    },

    onInvoiceSelect(invoice) {
      if (!invoice) {
        this.form.items = [];
        return;
      }

      // Seed items from the invoice's own line items as a starting point —
      // the user edits quantities down to just what's being returned/
      // corrected.
      this.form.items = (invoice.items || []).map((it) => ({
        product: it.product,
        hsn: it.hsn,
        quantity: it.quantity,
        price: it.price,
        taxPercent: it.taxPercent,
        amount: it.amount,
      }));
    },

    addItem() {
      this.form.items.push({
        product: "",
        hsn: "",
        quantity: 1,
        price: 0,
        taxPercent: 18,
        amount: 0,
      });
    },

    removeItem(i) {
      if (this.form.items.length === 1) return;
      this.form.items.splice(i, 1);
    },

    calcItem(i) {
      const item = this.form.items[i];
      if (!item) return;

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      item.amount = +(qty * price).toFixed(2);
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },

    reset() {
      this.selectedInvoice = null;
      this.form.reason = "SALES_RETURN";
      this.form.note = "";
      this.form.items = [];
      this.fetchNextNumber();
    },

    async submit() {
      if (!this.$refs.form.validate()) return;
      if (!this.selectedInvoice) {
        this.$toast?.error("Please select an invoice");
        return;
      }

      this.loading = true;

      try {
        const payload = {
          creditNoteNumber: this.form.creditNoteNumber,
          creditNoteDate: this.form.creditNoteDate,
          invoiceId: this.selectedInvoice._id,
          reason: this.form.reason,
          items: this.form.items,
          note: this.form.note,
        };

        const res = await this.$store.dispatch("creditNotes/createCreditNote", payload);

        this.$toast?.success("Credit note created successfully");
        this.$router.push(`/credit-notes/${res.creditNote._id}/view`);
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to create credit note",
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.cn-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.cn-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
}

.cn-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.cn-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.cn-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.cn-invoice-summary {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.cn-invoice-summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 13.5px;
  color: #475569;
  padding: 3px 0;
}

.cn-invoice-summary-row--highlight {
  font-weight: 700;
  color: #0f172a;
  border-top: 1px solid #e2e8f0;
  margin-top: 4px;
  padding-top: 6px;
}

.cn-item-row {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 14px 14px;
  margin-bottom: 12px;
}

.cn-item-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.cn-item-index {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.cn-item-remove {
  margin: -4px -4px 0 0;
}

.cn-item-row-foot {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
}

.cn-item-amount-label {
  font-size: 12px;
  color: #64748b;
}

.cn-item-amount-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.cn-add-item-btn {
  background: #eef3fb !important;
  color: #0a5a90 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.cn-total-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 4px 0;
  color: #475569;
}

.cn-total-row--grand {
  font-weight: 700;
  font-size: 16px;
  color: #0f172a;
  border-top: 1px solid #eef1f5;
  margin-top: 4px;
  padding-top: 8px;
}

.cn-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
